import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { AxiosError } from 'axios'
import React, { useEffect, useRef, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import {
  createSetHideToast,
  createSetIsToastOpen,
} from 'src/app/Toast/redux/toast.actions'
import {
  selectBulkToast,
  selectFailureColorSchemeFromState,
  selectForceHideToast,
  selectHidableToastNameFromState,
  selectHideImageFromState,
  selectImageFromState,
  selectInProgressColorSchemeFromState,
  selectIsDarkBackground,
  selectIsInfinite,
  selectIsToastOpenFromState,
  selectPartialSuccessColorSchemeFromState,
  selectToastMessageFromState,
} from 'src/app/Toast/redux/toast.selectors'
import { ReceiptAutoMatchPushTaskStatus } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.reducers'
import { selectReceiptsAutoMatchPushTaskListFromState } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import AppName from 'src/components/AppName/AppName'
import CloseButton, {
  CloseButtonDesigns,
} from 'src/components/CloseButton/CloseButton'
import SvgCheck from 'src/components/Svgs/SvgCheck'
import TestId from 'src/components/TestId/TestId'
import { DownloadStatus } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import {
  OrganizationInviteResponse,
  SaasTierType,
} from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { VirtualCardPushTaskListModel } from 'src/models/virtualCardPushTask.models'
import Lnk from 'src/navigation/Lnk'
import {
  accountOverviewUrl,
  activityUrl,
  billPayDetailUrl,
  peopleInvitedToOrgUrl,
  peopleMembersUrl,
  virtualCardDetailUrl,
} from 'src/navigation/urls'
import {
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'
import { LeftToRight } from 'src/style/keyframeUtils'
import { BackendErrorPayload } from 'src/utils/error.utils'
import { css } from 'styled-components'

const bold = css`
  ${getFontCss(
    14,
    styleConstants.colors.white,
    styleConstants.fontFamilies.medium,
  )};
`

interface PostResetPasswordToastMessageProps {
  email: string
}
export const PostResetPasswordToastMessage: React.FC<
  PostResetPasswordToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate('Instructions sent to ${email}', {
    email: (
      <span id="pwdReset" css={bold}>
        {props.email}
      </span>
    ),
  })
}

interface UpdateVirtualCardToastMessageProps {}
export const UpdateVirtualCardToastMessage: React.FC<
  UpdateVirtualCardToastMessageProps
> = (): React.ReactNode => {
  const translate = useTranslate()
  return (
    <span id="updateVcnToast" css={bold}>
      {translate('Success!')}
    </span>
  )
}

interface CreateVirtualCardToastMessageProps {
  virtualCard: VirtualCardListModel
  users: Record<string, UserListModel>
}
export const CreateVirtualCardToastMessage: React.FC<
  CreateVirtualCardToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    '${success} You have created ${virtualCardName} for ${recipientEmail}.',
    {
      success: (
        <span id="createVcnToast" css={bold}>
          {translate('Success!')}
        </span>
      ),
      virtualCardName: (
        <Lnk
          css={css`
            text-decoration: underline;
          `}
          to={virtualCardDetailUrl(props.virtualCard.id)}
          ariaLabelSource={'Virtual Card Details'}
          testId={'virtualCardDetails'}
        >
          {props.virtualCard.displayName}
        </Lnk>
      ),
      recipientEmail: props.users[props.virtualCard.recipientId].email,
    },
  )
}

interface CreateVirtualCardAndInvitedUserToastMessageProps {
  virtualCard: VirtualCardListModel
  users: Record<string, UserListModel>
}
export const CreateVirtualCardAndInvitedUserToastMessage: React.FC<
  CreateVirtualCardAndInvitedUserToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    '${success} You have created ${virtualCardName} for ${recipientEmail} and invited them to your org.',
    {
      success: (
        <span id="createVcnToast" css={bold}>
          {translate('Success!')}
        </span>
      ),
      virtualCardName: (
        <Lnk
          css={css`
            text-decoration: underline;
          `}
          to={virtualCardDetailUrl(props.virtualCard.id)}
          ariaLabelSource={'Virtual Card Details'}
          testId={'virtualCardDetails'}
        >
          {props.virtualCard.displayName}
        </Lnk>
      ),
      recipientEmail:
        props.users[props.virtualCard.recipientId]?.email ||
        props.virtualCard?.recipient?.email ||
        '',
    },
  )
}

export const CreateVirtualCardPendingFinalApprovalToastMessage: React.FC =
  (): React.ReactNode => {
    const translate = useTranslate()
    return translate(
      '${success} The virtual card request is now being sent to the Approver for final approval.',
      {
        success: (
          <span id="createVcnPendingApprovalToast" css={bold}>
            {translate('Success!')}
          </span>
        ),
      },
    )
  }

export const UpdateVirtualCardPendingFinalApprovalToastMessage: React.FC =
  (): React.ReactNode => {
    const translate = useTranslate()
    return translate(
      '${success} The virtual card updates are now being sent to the Approver for final approval.',
      {
        success: (
          <span id="createVcnPendingApprovalToast" css={bold}>
            {translate('Success!')}
          </span>
        ),
      },
    )
  }

interface CreateBulkVirtualCardToastMessageProps {
  displayName: string
  virtualCardPushTasks: VirtualCardPushTaskListModel[]
}
export const CreateBulkVirtualCardToastMessage: React.FC<
  CreateBulkVirtualCardToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <>
      <div>
        {translate('Creating ${virtualCardName} virtual cards', {
          virtualCardName: (
            <span id="createBulkVcnToast" css={bold}>
              {props.displayName}
            </span>
          ),
        })}
      </div>
      <div
        css={css`
          ${getFontCss(
            10,
            styleConstants.colors.white,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate('${creating} of ${created} created', {
          creating: (
            <>
              {
                props.virtualCardPushTasks.filter(
                  (task) => task.status !== 'Initiated',
                ).length
              }
            </>
          ),
          created: <>{props.virtualCardPushTasks.length}</>,
        })}
      </div>
    </>
  )
}

interface UpdateBulkVirtualCardInProgressToastMessageProps {
  updating: number
  total: number
}
export const UpdateBulkVirtualCardInProgressToastMessage: React.FC<
  UpdateBulkVirtualCardInProgressToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <>
      {translate('Updating Virtual Cards')}
      <div
        css={css`
          ${getFontCss(
            10,
            styleConstants.colors.white,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate('${updating} of ${total} updated', {
          updating: <>{props.updating}</>,
          total: <>{props.total}</>,
        })}
      </div>
    </>
  )
}

export const UpdateBulkVirtualCardCompleteToastMessage: React.FC =
  (): React.ReactNode => {
    const translate = useTranslate()
    return (
      <TestId testId={'bulkUpdateSuccessToast'}>
        {translate('Success! Your updates are complete')}
      </TestId>
    )
  }

interface UpdateBulkVirtualCardPartialCompleteToastMessageProps {
  updated: number
  total: number
}
export const UpdateBulkVirtualCardPartialCompleteToastMessage: React.FC<
  UpdateBulkVirtualCardPartialCompleteToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <div
      css={css`
        ${getFontCss(
          14,
          themeConstants.colors.font_primary,
          styleConstants.fontFamilies.regular,
        )};
      `}
    >
      {translate(
        'Only ${updated} of ${total} virtual cards could be updated. Check your email for an update summary.',
        {
          updated: <>{props.updated}</>,
          total: <>{props.total}</>,
        },
      )}
    </div>
  )
}

interface DeactivateBulkVirtualCardInProgressToastMessageProps {
  deactivating: number
  total: number
}
export const DeactivateBulkVirtualCardInProgressToastMessage: React.FC<
  DeactivateBulkVirtualCardInProgressToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <>
      {translate('Deactivating Virtual Cards')}
      <div
        css={css`
          ${getFontCss(
            10,
            styleConstants.colors.white,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate('${deactivating} of ${total} deactivated', {
          deactivating: <>{props.deactivating}</>,
          total: <>{props.total}</>,
        })}
      </div>
    </>
  )
}

export const DeactivateBulkVirtualCardCompleteToastMessage: React.FC =
  (): React.ReactNode => {
    const translate = useTranslate()
    return (
      <TestId testId={'bulkDeactivateSuccessToast'}>
        {translate('Success! Cards are deactivated')}
      </TestId>
    )
  }

interface DeactivateBulkVirtualCardPartialCompleteToastMessageProps {
  deactivated: number
  total: number
}
export const DeactivateBulkVirtualCardPartialCompleteToastMessage: React.FC<
  DeactivateBulkVirtualCardPartialCompleteToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <div
      css={css`
        ${getFontCss(
          14,
          themeConstants.colors.font_primary,
          styleConstants.fontFamilies.regular,
        )};
      `}
    >
      {translate(
        'Only ${deactivated} of ${total} virtual cards could be deactivated. Check your email for an update summary.',
        {
          deactivated: <>{props.deactivated}</>,
          total: <>{props.total}</>,
        },
      )}
    </div>
  )
}

interface CloseBulkVirtualCardInProgressToastMessageProps {
  closing: number
  total: number
}
export const CloseBulkVirtualCardInProgressToastMessage: React.FC<
  CloseBulkVirtualCardInProgressToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <>
      {translate('Closing Virtual Cards')}
      <div
        css={css`
          ${getFontCss(
            10,
            styleConstants.colors.white,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate('${closing} of ${total} closed', {
          closing: <>{props.closing}</>,
          total: <>{props.total}</>,
        })}
      </div>
    </>
  )
}

export const CloseBulkVirtualCardCompleteToastMessage: React.FC =
  (): React.ReactNode => {
    const translate = useTranslate()
    return (
      <TestId testId={'bulkCloseSuccessToast'}>
        {translate('${bold} Your cards have been closed.', {
          bold: <span css={bold}>{translate('Success!')}</span>,
        })}
      </TestId>
    )
  }

interface CloseBulkVirtualCardPartialCompleteToastMessageProps {
  closed: number
  total: number
}
export const CloseBulkVirtualCardPartialCompleteToastMessage: React.FC<
  CloseBulkVirtualCardPartialCompleteToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <div
      css={css`
        ${getFontCss(
          14,
          themeConstants.colors.font_primary,
          styleConstants.fontFamilies.regular,
        )};
      `}
    >
      {translate(
        'Only ${closed} of ${total} virtual cards could be closed. Check your email for an update summary.',
        {
          closed: <>{props.closed}</>,
          total: <>{props.total}</>,
        },
      )}
    </div>
  )
}

interface CreateBulkVirtualCardSuccessToastMessageProps {
  displayName: string
  virtualCardPushTasks: VirtualCardPushTaskListModel[]
}
export const CreateBulkVirtualCardSuccessToastMessage: React.FC<
  CreateBulkVirtualCardSuccessToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <div>
      {translate(
        'Success! You have created ${virtualCardName} virtual cards for ${num} recipients',
        {
          virtualCardName: (
            <span id="createBulkVcnSuccessToast" css={bold}>
              {props.displayName}
            </span>
          ),
          num: props.virtualCardPushTasks.length.toString(),
        },
      )}
    </div>
  )
}

interface CreateBulkVirtualCardAndInvitedUsersSuccessToastMessageProps {
  displayName: string
  virtualCardPushTasks: VirtualCardPushTaskListModel[]
}
export const CreateBulkVirtualCardAndInvitedUsersSuccessToastMessage: React.FC<
  CreateBulkVirtualCardAndInvitedUsersSuccessToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <div>
      {translate(
        'Success! You have created ${virtualCardName} virtual cards for ${num} recipients, and your org invites have been sent.',
        {
          virtualCardName: (
            <span id="createBulkVcnSuccessToast" css={bold}>
              {props.displayName}
            </span>
          ),
          num: props.virtualCardPushTasks.length.toString(),
        },
      )}
    </div>
  )
}

interface CreateBulkVirtualCardFailureToastMessageProps {
  displayName: string
}
export const CreateBulkVirtualCardFailureToastMessage: React.FC<
  CreateBulkVirtualCardFailureToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <div>
      {translate(
        'Oops! Some of the ${virtualCardName} cards failed to be created. You’ve been emailed the details.',
        {
          virtualCardName: (
            <span id="createBulkVcnFailToast" css={bold}>
              {props.displayName}
            </span>
          ),
        },
      )}
    </div>
  )
}

interface RequestVirtualCardToastMessageProps {
  virtualCard: VirtualCardListModel
  users: Record<string, UserListModel>
}
export const RequestVirtualCardToastMessage: React.FC<
  RequestVirtualCardToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    '${newVirtualCardRequested} You have requested ${virtualCardName} from ${cardholderEmail}.',
    {
      newVirtualCardRequested: (
        <span id="requestVcnToast" css={bold}>
          {translate('New Virtual Card Requested!')}
        </span>
      ),
      virtualCardName: (
        <Lnk
          css={css`
            text-decoration: underline;
          `}
          to={virtualCardDetailUrl(props.virtualCard.id)}
          ariaLabelSource={'Virtual Card Details'}
          testId={'virtualCardDetails'}
        >
          {props.virtualCard.displayName}
        </Lnk>
      ),
      cardholderEmail: props.users[props.virtualCard.cardholderId].email,
    },
  )
}

interface RequestBulkVirtualCardToastMessageProps {
  virtualCardId: string
  displayName: string
  recipient: string
  virtualCardPushTasks: VirtualCardPushTaskListModel[]
}
export const RequestBulkVirtualCardToastMessage: React.FC<
  RequestBulkVirtualCardToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    '${newVirtualCardRequested} You have requested ${virtualCardName} from ${cardholderEmail}.',
    {
      newVirtualCardRequested: (
        <span id="requestBulkVcnToast" css={bold}>
          {translate('New Virtual Card Requested!')}
        </span>
      ),
      virtualCardName: (
        <Lnk
          css={css`
            text-decoration: underline;
          `}
          to={virtualCardDetailUrl(props.virtualCardId)}
          ariaLabelSource={'Virtual Card Details'}
          testId={'virtualCardDetails'}
        >
          {props.displayName}
        </Lnk>
      ),
      cardholderEmail: props.recipient,
    },
  )
}

interface CreateBudgetToastMessageProps {
  creditCard: CreditCardListModel
  users: Record<string, UserListModel>
  emails: boolean
}
export const CreateBudgetToastMessage: React.FC<
  CreateBudgetToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return (
    <>
      {translate(
        '${budgetCreated} You have successfully created ${budgetName}',
        {
          budgetCreated: (
            <span id="createBudgetToast" css={bold}>
              {translate('Budget Created!')}
            </span>
          ),
          budgetName: (
            <Lnk
              css={css`
                text-decoration: underline;
              `}
              to={accountOverviewUrl(props.creditCard.id)}
              ariaLabelSource={'Account Overview'}
              testId={'accountOverview'}
            >
              {props.creditCard.displayName}
            </Lnk>
          ),
        },
      )}
      {props.emails &&
        translate(' for ${recipientEmail}.', {
          recipientEmail:
            props.users[props.creditCard.userId].email || 'unknown',
        })}
    </>
  )
}

interface UpdateBudgetToastMessageProps {
  creditCard: CreditCardListModel
  users: Record<string, UserListModel>
}
export const UpdateBudgetToastMessage: React.FC<
  UpdateBudgetToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    '${budgetUpdated} You have successfully updated ${budgetName} for ${recipientEmail}.',
    {
      budgetUpdated: (
        <span id="updateBudgetToast" css={bold}>
          {translate('Budget updated!')}
        </span>
      ),
      budgetName: (
        <Lnk
          css={css`
            text-decoration: underline;
          `}
          to={accountOverviewUrl(props.creditCard.id)}
          ariaLabelSource={'Account Overview'}
          testId={'accountOverview'}
        >
          {props.creditCard.displayName}
        </Lnk>
      ),
      recipientEmail: props.users[props.creditCard.userId].email,
    },
  )
}

interface RemoveUserFromBudgetToastMessageProps {
  creditCard: CreditCardListModel
  firstName: string | undefined
  lastName: string | undefined
  email: string
}
export const RemoveUserFromBudgetToastMessage: React.FC<
  RemoveUserFromBudgetToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  const displayName =
    props.firstName === '' && props.lastName === ''
      ? props.email
      : `${props.firstName} ${props.lastName}`
  return translate(
    "Success! You've removed ${budgetUser} from ${budgetName}.",
    {
      budgetUser: displayName,
      budgetName: props.creditCard.displayName,
    },
  )
}

interface RequestBudgetToastMessageProps {
  budgetName: string
  user: string
}
export const RequestBudgetToastMessage: React.FC<
  RequestBudgetToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    '${success} New Budget Requested! You have requested ${budgetName} from ${user}.',
    {
      success: (
        <span id="addContactToast" css={bold}>
          {translate('Success!')}
        </span>
      ),
      budgetName: props.budgetName,
      user: props.user,
    },
  )
}

interface ApproveBudgetSuccessToastMessageProps {
  budgetName: string
}
export const ApproveBudgetSuccessToastMessage: React.FC<
  ApproveBudgetSuccessToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return (
    <TestId testId={'approvedBudgetToast'}>
      {translate('You have approved ${budgetName}.', {
        budgetName: props.budgetName,
      })}
    </TestId>
  )
}

interface RejectBudgetSuccessToastMessageProps {
  budgetName: string
}
export const RejectBudgetSuccessToastMessage: React.FC<
  RejectBudgetSuccessToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return (
    <TestId testId={'rejectedBudgetToast'}>
      {translate('You have rejected ${budgetName}.', {
        budgetName: props.budgetName,
      })}
    </TestId>
  )
}

interface UpdateBudgetSuccessToastMessageProps {
  budgetName: string
}
export const UpdateBudgetSuccessToastMessage: React.FC<
  UpdateBudgetSuccessToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate('You have updated ${budgetName}.', {
    budgetName: props.budgetName,
  })
}

interface CancelBudgetSuccessToastMessageProps {
  budgetName: string
}
export const CancelBudgetSuccessToastMessage: React.FC<
  CancelBudgetSuccessToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate('You have cancelled ${budgetName}.', {
    budgetName: props.budgetName,
  })
}
interface InviteToOrganizationToastMessageProps {
  people: OrganizationInviteResponse[]
  repeat?: boolean
  guest?: boolean
}
export const InviteToOrganizationToastMessage: React.FC<
  InviteToOrganizationToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return props.people.length === 1
    ? translate(
        props.guest
          ? "${success} You've added ${numberInvites} person. ${invitedListLink}."
          : props.repeat
            ? "${success} You've re-sent an org invitation to ${inviteeNameOrEmail}."
            : "${success} You've invited ${numberInvites} person. ${invitedListLink}.",
        {
          success: (
            <span id="inviteToOrgToast" css={bold}>
              {translate('Success!')}
            </span>
          ),
          numberInvites: `${props.people.length}`,
          invitedListLink: props.guest ? (
            <Lnk
              css={css`
                text-decoration: underline;
              `}
              to={peopleMembersUrl()}
              ariaLabelSource={'See the org members list'}
              testId={'orgMembersList'}
            >
              {translate('See the org members list')}
            </Lnk>
          ) : (
            <Lnk
              css={css`
                text-decoration: underline;
              `}
              to={peopleInvitedToOrgUrl()}
              ariaLabelSource={'See the invited list'}
              testId={'invitedList'}
            >
              {translate('See the invited list')}
            </Lnk>
          ),
          inviteeNameOrEmail: (
            <span css={bold}>
              {props.people[0].invitee.firstName &&
              props.people[0].invitee.lastName
                ? `${props.people[0].invitee.firstName} ${props.people[0].invitee.lastName}`
                : `${props.people[0].invitee.email}`}
            </span>
          ),
        },
      )
    : translate(
        props.guest
          ? "${success} You've added ${numberInvites} people. ${invitedListLink}."
          : "${success} You've invited ${numberInvites} people. ${invitedListLink}.",
        {
          success: (
            <span id="inviteMultipleToOrgToast" css={bold}>
              {translate('Success!')}
            </span>
          ),
          numberInvites: `${props.people.length}`,
          invitedListLink: props.guest ? (
            <Lnk
              css={css`
                text-decoration: underline;
              `}
              to={peopleMembersUrl()}
              ariaLabelSource={'See the org members list'}
              testId={'orgMembersList'}
            >
              {translate('See the org members list')}
            </Lnk>
          ) : (
            <Lnk
              css={css`
                text-decoration: underline;
              `}
              to={peopleInvitedToOrgUrl()}
              ariaLabelSource={'See the invited list'}
              testId={'invitedList'}
            >
              {translate('See the invited list')}
            </Lnk>
          ),
        },
      )
}

interface DeclineOrganizationInviteToastMessageProps {
  organizationInvite: OrganizationInviteResponse
}
export const DeclineOrganizationInviteMessage: React.FC<
  DeclineOrganizationInviteToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    "You've declined the invitation to join ${inviterFirstName}'s organization ${organizationName}.",
    {
      inviterFirstName: (
        <span css={bold}>{props.organizationInvite.inviter.firstName}</span>
      ),
      organizationName: (
        <span id="declineOrgInviteToast" css={bold}>
          {props.organizationInvite.organization.name}
        </span>
      ),
    },
  )
}

interface CancelOrgInviteToastMessageProps {
  organizationInvite: OrganizationInviteResponse
}
export const CancelOrgInviteToastMessage: React.FC<
  CancelOrgInviteToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    "You've canceled the org invitation sent to ${inviteeNameOrEmail}.",
    {
      inviteeNameOrEmail: (
        <span id="cancelOrgInviteToast" css={bold}>
          {props.organizationInvite.invitee.firstName &&
          props.organizationInvite.invitee.lastName
            ? `${props.organizationInvite.invitee.firstName} ${props.organizationInvite.invitee.lastName}`
            : `${props.organizationInvite.invitee.email}`}
        </span>
      ),
    },
  )
}

interface RemoveUserFromOrganizationToastMessageProps {
  firstName: string
  lastName: string
  email: string
}
export const RemoveUserFromOrganizationToastMessage: React.FC<
  RemoveUserFromOrganizationToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  const displayName =
    props.firstName === '' && props.lastName === ''
      ? props.email
      : `${props.firstName} ${props.lastName}`
  return translate("${success} You've removed ${displayName}.", {
    success: (
      <span id="removeUserFromOrgToast" css={bold}>
        {translate('Success!')}
      </span>
    ),
    displayName: <span css={bold}>{displayName}</span>,
  })
}

interface DeactivateVirtualCardToastMessageProps {
  virtualCard: VirtualCardListModel
}
export const DeactivateVirtualCardToastMessage: React.FC<
  DeactivateVirtualCardToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate('You deactivated ${virtualCardName}', {
    virtualCardName: (
      <Lnk
        id="deactivateVcnToast"
        css={css`
          text-decoration: underline;
        `}
        to={virtualCardDetailUrl(props.virtualCard.id)}
        ariaLabelSource={'Virtual Card Details'}
        testId={'virtualCardDetails'}
      >
        {props.virtualCard.displayName}
      </Lnk>
    ),
  })
}

interface DeactivateBudgetToastMessageProps {
  budget: CreditCardListModel
}
export const DeactivateBudgetToastMessage: React.FC<
  DeactivateBudgetToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate('Success! You deactivated ${budgetName}', {
    budgetName: (
      <Lnk
        id="deactivateBudgetToast"
        css={css`
          text-decoration: underline;
        `}
        to={accountOverviewUrl(props.budget.id)}
        ariaLabelSource={'Budget Details'}
        testId={'budgetDetails'}
      >
        {props.budget.displayName}
      </Lnk>
    ),
  })
}

interface DeactivateBudgetFailureToastMessageProps {
  budget: CreditCardListModel
}
export const DeactivateBudgetFailureToastMessage: React.FC<
  DeactivateBudgetFailureToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    '${budgetName} could not be deactivated, please try again later',
    {
      budgetName: (
        <Lnk
          id="deactivateBudgetToast"
          css={css`
            text-decoration: underline;
          `}
          to={accountOverviewUrl(props.budget.id)}
          ariaLabelSource={'Budget Details'}
          testId={'budgetDetails'}
        >
          {props.budget.displayName}
        </Lnk>
      ),
    },
  )
}
interface UpdateOrgRoleToastMessageProps {
  userName: string
  userRole: React.ReactNode
  isCurrentUser?: boolean
}
export const UpdateOrgRoleToastMessage: React.FC<
  UpdateOrgRoleToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return translate(
    props.isCurrentUser
      ? "You've changed your role to ${userRole}"
      : "${success} You've updated ${userName}'s role and permissions.",
    {
      userName: <span css={bold}>{props.userName}</span>,
      userRole: (
        <span id="updateOrgRoleToast" css={bold}>
          {props.userRole}
        </span>
      ),
      success: <span css={bold}>{translate('Success!')}</span>,
    },
  )
}

interface ApproveExpensesToastMessageProps {
  expensesCount: number
}

export const ApproveExpensesToastMessage: React.FC<
  ApproveExpensesToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    props.expensesCount === 1
      ? 'You approved 1 expense'
      : 'You approved ${count} expenses',
    {
      count: props.expensesCount.toString(),
    },
  )
}

interface RejectExpensesToastMessageProps {
  expensesCount: number
}

export const RejectExpensesToastMessage: React.FC<
  RejectExpensesToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    props.expensesCount === 1
      ? 'You rejected 1 expense'
      : 'You rejected ${count} expenses',
    {
      count: props.expensesCount.toString(),
    },
  )
}

interface DeleteExpensesToastMessageProps {
  expensesCount: number
}

export const DeleteExpensesToastMessage: React.FC<
  React.PropsWithChildren<DeleteExpensesToastMessageProps>
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    props.expensesCount === 1
      ? 'You deleted 1 expense'
      : 'You deleted ${count} expenses',
    {
      count: props.expensesCount.toString(),
    },
  )
}

interface DeleteExpenseReportsToastMessageProps {
  expenseReportsCount: number
}

export const DeleteExpenseReportsToastMessage: React.FC<
  React.PropsWithChildren<DeleteExpenseReportsToastMessageProps>
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    props.expenseReportsCount === 1
      ? 'You deleted 1 reimbursement report'
      : 'You deleted ${count} reimbursement reports',
    {
      count: props.expenseReportsCount.toString(),
    },
  )
}

export const UpdateExpenseToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate('You updated 1 expense')
}

export const SubmitExpenseReportToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate('You submitted your reimbursement report')
}

interface MarkExpenseReportsAsPaidToastMessageProps {
  expenseReportsCount: number
  allReports?: boolean
}

export const MarkExpenseReportsAsPaidToastMessage: React.FC<
  MarkExpenseReportsAsPaidToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    !props.allReports
      ? 'You marked this report as paid'
      : props.expenseReportsCount === 1
        ? 'You marked 1 reimbursement report as paid'
        : 'You marked ${count} reimbursement reports as paid',
    {
      count: props.expenseReportsCount.toString(),
    },
  )
}

export const DownloadToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return (
    <>
      <div css={bold}>{translate('Your download is being prepared')}</div>
      <div
        css={css`
          ${getFontCss(
            10,
            styleConstants.colors.white,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate(
          'Please do not close this browser tab. You can continue to navigate ${appName}.',
          {
            appName: <AppName />,
          },
        )}
      </div>
    </>
  )
}

export const DownloadFromEmailToastMessage: React.FC<{
  status: DownloadStatus
}> = (props): React.ReactNode => {
  const translate = useTranslate()

  return translate(
    props.status === DownloadStatus.Success
      ? '${bolded} Your report has downloaded.'
      : '${bolded} Your download could not be generated. Please generate a new one.',
    {
      bolded: (
        <span css={bold}>
          {translate(
            props.status === DownloadStatus.Success ? 'Success!' : 'Oops!',
          )}
        </span>
      ),
    },
  )
}

export const RemovedCognitoDeviceMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate("You've removed a trusted device", {
    appName: <AppName />,
  })
}

export const NewVerificationCodeSentToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    'A new verification code has been sent. Log into your bank account to find the code beginning with "EX*".',
  )
}

export const CreateCreditCardNeedsVerificationToastMessage =
  (): React.ReactNode => {
    const translate = useTranslate()
    return translate(
      "You've registered your new payment method, but it needs to be verified before being activated.",
    )
  }

export const CreateCreditCardNeedsValidationToastMessage =
  (): React.ReactNode => {
    const translate = useTranslate()
    return translate(
      "You've registered your new payment method, but it needs to be validated before being activated.",
    )
  }

export const CreateCreditCardVerificationSuccessMessage =
  (): React.ReactNode => {
    const translate = useTranslate()
    return translate("You've successfully activated a payment method!")
  }

export const IntegrationsFeedbackSuccessToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate('Thank you for sharing your feedback!')
}

export const ReceiptReminderSuccessToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate('${success} You sent a bulk receipt reminder.', {
    success: <span css={bold}>{translate('Success!')}</span>,
  })
}

interface SingleReceiptReminderSuccessToastMessageProps {
  name?: string
}

export const SingleReceiptReminderSuccessToastMessage: React.FC<
  SingleReceiptReminderSuccessToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return props.name === undefined
    ? translate('${success} You sent a single receipt reminder.', {
        success: <span css={bold}>{translate('Success!')}</span>,
      })
    : translate('${success} You sent a single receipt reminder to ${name}.', {
        success: <span css={bold}>{translate('Success!')}</span>,
        name: <span css={bold}>{props.name}</span>,
      })
}

export const UpdateExpenseReportNameSuccessToastMessage =
  (): React.ReactNode => {
    const translate = useTranslate()
    return translate('You saved your new report name')
  }

interface OrgIntegrationToastMessageProps {
  orgIntegrationDisplayName: string
}

export const SyncTransactionsSuccessToastMessage: React.FC<
  OrgIntegrationToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    'Success! You have synced your transactions with ${orgIntegration}.',
    {
      orgIntegration: <span>{props.orgIntegrationDisplayName}</span>,
    },
  )
}

interface TransactionStatusToastMessageProps {
  count?: number
  status: string
}

export const SetTransactionStatusSuccessToastMessage: React.FC<
  TransactionStatusToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    'Success! You have set ${count} transactions to ${status}.',
    {
      count: <span>{props.count}</span>,
      status: <span>{props.status}</span>,
    },
  )
}

export const SetTransactionStatusFailureToastMessage: React.FC<
  TransactionStatusToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    'Oops! There was an issue setting your transactions to ${status}. Please try again later.',
    {
      status: <span>{props.status}</span>,
    },
  )
}

interface UnableToConnectToNetsuiteToastMessageProps {
  connectedPlatformName?: string
}

export const UnableToConnectToNetsuiteToastMessage: React.FC<
  UnableToConnectToNetsuiteToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return (
    <>
      {props.connectedPlatformName
        ? // TODO: use error from network request
          translate('Could not establish connection to ${connectedPlatform}', {
            connectedPlatform: props.connectedPlatformName,
          })
        : translate('Could not establish a connection')}
    </>
  )
}

export const ThankYouForSettingUpYourOrgToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate('Thank you for setting up your org!')
}

export const ThankYouForConfirmingYourOrgToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate('Thank you for confirming your org!')
}

type CreateBulkReceiptAutoMatchToastMessageProps = {
  isCreatingExpenses?: boolean
}

export const CreateBulkReceiptAutoMatchToastMessage: React.FC<
  CreateBulkReceiptAutoMatchToastMessageProps
> = (props: CreateBulkReceiptAutoMatchToastMessageProps): React.ReactNode => {
  const translate = useTranslate()

  const receiptsAutoMatchPushTaskList = useSelector(
    selectReceiptsAutoMatchPushTaskListFromState,
  )
  const completedTasks = receiptsAutoMatchPushTaskList.filter(
    (task) => task.status !== ReceiptAutoMatchPushTaskStatus.PENDING,
  )

  return (
    <>
      <div>
        {translate(
          props.isCreatingExpenses
            ? 'Creating expenses'
            : 'Auto-matching receipts',
        )}
      </div>
      <div
        css={css`
          ${getFontCss(
            10,
            styleConstants.colors.white,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate('${processed} of ${total} processed', {
          processed: completedTasks.length.toString(),
          total: receiptsAutoMatchPushTaskList.length.toString(),
        })}
      </div>
    </>
  )
}

type CreateBulkReceiptAutoMatchSuccessToastMessageProps = {
  isCreatingExpenses?: boolean
}
export const CreateBulkReceiptAutoMatchSuccessToastMessage: React.FC<
  CreateBulkReceiptAutoMatchSuccessToastMessageProps
> = (
  props: CreateBulkReceiptAutoMatchSuccessToastMessageProps,
): React.ReactNode => {
  const translate = useTranslate()

  return translate(
    props.isCreatingExpenses
      ? 'Success! We created expenses from your uploaded receipts'
      : 'Your receipts were auto-matched! You can see them ${notificationLink}',
    {
      notificationLink: (
        <Lnk
          to={{
            pathname: activityUrl(),
            search: '?tab=notifications',
            hash: '',
            state: {},
          }}
          ariaLabelSource={'here'}
          css={css`
            text-decoration: underline;
          `}
          testId={'notificationsLink'}
        >
          {translate('here')}
        </Lnk>
      ),
    },
  )
}

type CreateBulkReceiptAutoMatchFailureToastMessageProps = {
  isCreatingExpenses?: boolean
}
export const CreateBulkReceiptAutoMatchFailureToastMessage: React.FC<
  CreateBulkReceiptAutoMatchToastMessageProps
> = (
  props: CreateBulkReceiptAutoMatchFailureToastMessageProps,
): React.ReactNode => {
  const translate = useTranslate()

  return translate(
    props.isCreatingExpenses
      ? 'Oops! We could not create expenses from your uploaded receipt(s). Please manually add your expenses'
      : 'Oops! We could not match your receipt(s) to a transaction, but our system will continue trying to find a match for the next three days.',
  )
}

interface CreateBulkReceiptAutoMatchPartialToastMessageProps {
  successful: number
  failed: number
  isCreatingExpenses?: boolean
}
export const CreateBulkReceiptAutoMatchPartialToastMessage: React.FC<
  CreateBulkReceiptAutoMatchPartialToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <span
      css={css`
        ${getFontCss(
          14,
          styleConstants.colors.black,
          styleConstants.fontFamilies.regular,
        )};
      `}
    >
      {translate(
        props.isCreatingExpenses
          ? 'We could not create expenses from ${failed} of your uploaded receipt(s). Please manually add your expenses.'
          : 'We could only auto-match ${successful} receipt(s). ${notificationLink}. ${failed} receipt(s) did not match to a transaction, but our system will continue trying to find a match for the next three days.',
        {
          successful: props.successful.toString(),
          failed: props.failed.toString(),
          notificationLink: (
            <Lnk
              to={{
                pathname: activityUrl(),
                search: '?tab=notifications',
                hash: '',
                state: {},
              }}
              ariaLabelSource={'here'}
              css={css`
                text-decoration: underline;
              `}
              testId={'notificationsLink'}
            >
              {translate('View here')}
            </Lnk>
          ),
        },
      )}
    </span>
  )
}

interface ResendVirtualCardToastSuccessMessageProps {
  name: string | undefined
}
export const ResendVirtualCardToastSuccessMessage: React.FC<
  ResendVirtualCardToastSuccessMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate('${success} You sent a virtual card reminder to ${name}', {
    success: <span css={bold}>{translate('Success!')}</span>,
    name: props.name ?? '',
  })
}

export const ResendVirtualCardToastLimitMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate('Virtual card reminders can only be sent once per hour')
}

export const ResendVirtualCardToastFailureMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    'A virtual card reminder could not be sent, please try again later',
  )
}

export const ResendVirtualCardToastExpenseCategoriesRequiredButMissingMessage =
  (): React.ReactNode => {
    const translate = useTranslate()
    return translate(
      'A virtual card reminder could not be sent, expense categories are required. Please update the virtual card expense categories and try again later',
    )
  }

interface CloseVirtualCardToastMessageProps {
  displayName: string
  id: string
}
export const CloseVirtualCardToastMessage: React.FC<
  CloseVirtualCardToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return translate('${success} You have permanently closed ${displayName}.', {
    success: (
      <span id="closeVirtualCardToast" css={bold}>
        {translate('Success!')}
      </span>
    ),
    displayName: (
      <Lnk
        id="closeVcnToast"
        css={css`
          text-decoration: underline;
          ${bold}
        `}
        to={virtualCardDetailUrl(props.id)}
        ariaLabelSource={'Virtual Card Details'}
        testId={'virtualCardDetails'}
      >
        {props.displayName}
      </Lnk>
    ),
  })
}

interface CloseAccountToastMessageProps {
  displayName: string
  id: string
}
export const CloseAccountToastMessage: React.FC<
  CloseAccountToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return translate(
    '${displayName} has been queued to be permanently closed. Please check back in a few minutes.',
    {
      displayName: (
        <Lnk
          id="closeAccountToast"
          css={css`
            text-decoration: underline;
            ${bold}
          `}
          to={accountOverviewUrl(props.id)}
          ariaLabelSource={'Account Overview'}
          testId={'accountOverview'}
        >
          {props.displayName}
        </Lnk>
      ),
    },
  )
}

export const VerifiedSecondaryEmailToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return (
    <TestId testId={'secondaryEmailSuccessToast'}>
      {translate('${success} Your secondary email has been added.', {
        success: <span css={bold}>{translate('Success!')}</span>,
      })}
    </TestId>
  )
}

export const RemoveTransactionSuccessToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return (
    <TestId testId={'removeTransactionSuccessToast'}>
      {translate('${success} You have removed 1 transaction.', {
        success: <span css={bold}>{translate('Success!')}</span>,
      })}
    </TestId>
  )
}

export const EnableAIExpenseCategoriesToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return (
    <TestId testId={'enableAIExpenseCategoriesToast'}>
      <div css={bold}>{translate('Setting up AI-powered coding')}</div>
      {translate(
        "We're analyzing your organization's transaction history so AI can help categorize your expenses. This usually takes less than 10 minutes.",
      )}
    </TestId>
  )
}

interface BulkTransactionUpdateSuccessToastMessageProps {
  count: number
}

export const BulkTransactionUpdateSuccessToastMessage: React.FC<
  BulkTransactionUpdateSuccessToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    props.count === 1
      ? '${success} You have updated ${count} transaction.'
      : '${success} You have updated ${count} transactions.',
    {
      count: <span>{props.count}</span>,
      success: <span css={bold}>{translate('Success!')}</span>,
    },
  )
}

interface CreateBillPayVirtualCardToastMessageProps {
  virtualCard: VirtualCardListModel
  shared: boolean
}
export const CreateBillPayVirtualCardToastMessage: React.FC<
  CreateBillPayVirtualCardToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    props.shared
      ? '${bold} A bill pay card for ${vendorName} was created and shared for invoice payment.'
      : '${bold} A bill pay card for ${vendorName} was created for invoice payment.',
    {
      bold: (
        <span
          id="createBillPayToast"
          data-testid="createBillPayToast"
          css={bold}
        >
          {translate('Success!')}
        </span>
      ),
      vendorName: (
        <Lnk
          css={css`
            text-decoration: underline;
            font-weight: bold;
          `}
          to={billPayDetailUrl(props.virtualCard.id)}
          ariaLabel={
            props.virtualCard.billPayMeta?.vendorName ??
            props.virtualCard.displayName
          }
          testId={'toastVendorName'}
        >
          {props.virtualCard.billPayMeta?.vendorName ??
            props.virtualCard.displayName}
        </Lnk>
      ),
    },
  )
}

interface ApproveBillPaySuccessToastMessageProps {
  vendor: string
}
export const ApproveBillPaySuccessToastMessage: React.FC<
  ApproveBillPaySuccessToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return (
    <TestId testId={'approvedBillPayToast'}>
      {translate('${success} A bill pay card for ${vendor} was approved.', {
        success: <span css={bold}>{translate('Success!')}</span>,
        vendor: <span css={bold}>{props.vendor}</span>,
      })}
    </TestId>
  )
}

interface RejectBillPaySuccessToastMessageProps {}
export const RejectBillPaySuccessToastMessage: React.FC<
  RejectBillPaySuccessToastMessageProps
> = (): React.ReactNode => {
  const translate = useTranslate()
  return (
    <TestId testId={'rejectedBillPayToast'}>
      {translate('${success} A bill pay card was rejected.', {
        success: <span css={bold}>{translate('Success!')}</span>,
      })}
    </TestId>
  )
}

interface BackendErrorToastMessageProps {
  error: AxiosError<BackendErrorPayload<string>> | BackendErrorPayload<string>
}
export const BackendErrorToastMessage: React.FC<
  BackendErrorToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  const payload: BackendErrorPayload<string> | undefined =
    (props.error as AxiosError<BackendErrorPayload<string>>)?.response?.data ??
    (props.error as BackendErrorPayload<string>)

  return (
    <>
      {payload.error ??
        translate('Sorry, something went wrong. Please try again later.')}
    </>
  )
}

interface AssignCardFeedCardsSuccessToastMessageProps {
  assignedCount: number
}
export const AssignCardFeedCardsSuccessToastMessage: React.FC<
  AssignCardFeedCardsSuccessToastMessageProps
> = ({ assignedCount }): React.ReactElement => {
  const translate = useTranslate()
  return (
    <div>
      {translate(
        assignedCount === 1
          ? '${success} 1 card has been assigned with an account owner.'
          : '${success} ${count} cards have been assigned with an account owner.',
        {
          success: <span css={bold}>{translate('Success!')}</span>,
          count: `${assignedCount}`,
        },
      )}
    </div>
  )
}

interface ReinviteCardholderSuccessToastMessageProps {
  recipient: string
}
export const ReinviteCardholderSuccessToastMessage: React.FC<
  ReinviteCardholderSuccessToastMessageProps
> = ({ recipient }): React.ReactElement => {
  const translate = useTranslate()
  return (
    <div>
      {translate('${success} Invitation resent to ${recipient}.', {
        success: <span css={bold}>{translate('Success!')}</span>,
        recipient,
      })}
    </div>
  )
}

export const ReinviteCardholderErrorToastMessage: React.FC =
  (): React.ReactElement => {
    const translate = useTranslate()
    return (
      <div>
        {translate(
          'Something went wrong resending the invitation. Please try again.',
        )}
      </div>
    )
  }

interface UnassignCardFeedCardSuccessToastMessageProps {
  user: string
  cardName: string
}
export const UnassignCardFeedCardSuccessToastMessage: React.FC<
  UnassignCardFeedCardSuccessToastMessageProps
> = ({ user, cardName }): React.ReactElement => {
  const translate = useTranslate()
  return (
    <div>
      {translate('${success} ${user} is now unassigned from ${cardName}.', {
        success: <span css={bold}>{translate('Success!')}</span>,
        user,
        cardName,
      })}
    </div>
  )
}

interface ConnectToAmericanExpressBankFeedSuccessMessageProps {}
export const ConnectToAmericanExpressBankFeedSuccessMessage: React.FC<
  ConnectToAmericanExpressBankFeedSuccessMessageProps
> = (): React.ReactNode => {
  const translate = useTranslate()

  return (
    <div>
      {translate('${success} You have connected to American Express.', {
        success: <span css={bold}>{translate('Success!')}</span>,
      })}
    </div>
  )
}

interface DisconnectFromAmericanExpressBankFeedSuccessMessageProps {}
export const DisconnectFromAmericanExpressBankFeedSuccessMessage: React.FC<
  DisconnectFromAmericanExpressBankFeedSuccessMessageProps
> = (): React.ReactNode => {
  const translate = useTranslate()

  return (
    <div>
      {translate('${success} You have disconnected from American Express.', {
        success: <span css={bold}>{translate('Success!')}</span>,
      })}
    </div>
  )
}

interface ExpenseCategoryLabelsBulkUploadSuccessToastMessageProps {
  count: string
}
export const ExpenseCategoryLabelsBulkUploadSuccessToastMessage: React.FC<
  ExpenseCategoryLabelsBulkUploadSuccessToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()

  return (
    <TestId testId={'expenseCategoryLabelsBulkUploadSuccessToast'}>
      {translate('${success} You successfully created ${count} new label(s).', {
        success: <span css={bold}>{translate('Success!')}</span>,
        count: props.count,
      })}
    </TestId>
  )
}

export const Choose2faMethodSuccessToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate('${success} Default method has been saved.', {
    success: <span css={bold}>{translate('Success!')}</span>,
  })
}

export const AsyncBulkTransactionUpdateInitiatedToastMessage: React.FC =
  (): React.ReactNode => {
    const translate = useTranslate()
    return (
      <TestId testId={'bulkTransactionUpdateInitiatedToast'}>
        {translate(
          'Your transaction updates are in progress. Thank you for your patience.',
        )}
      </TestId>
    )
  }

interface AsyncBulkTransactionUpdateToastMessageProps {
  partial: number
  total: number
}

export const AsyncBulkTransactionUpdateToastMessage: React.FC<
  AsyncBulkTransactionUpdateToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return (
    <TestId testId={'bulkTransactionUpdateInProgressToast'}>
      {translate('Updating Transactions')}
      <div
        css={css`
          ${getFontCss(
            10,
            styleConstants.colors.white,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate('${updating} of ${total} updated', {
          updating: <>{props.partial}</>,
          total: <>{props.total}</>,
        })}
      </div>
    </TestId>
  )
}

export const AsyncBulkTransactionUpdateCompleteToastMessage: React.FC =
  (): React.ReactNode => {
    const translate = useTranslate()
    return (
      <TestId testId={'bulkTransactionUpdateSuccessToast'}>
        {translate('${success} Your updates are complete.', {
          success: <span css={bold}>{translate('Success!')}</span>,
        })}
      </TestId>
    )
  }

export const AsyncBulkTransactionUpdatePartialCompleteToastMessage: React.FC<
  AsyncBulkTransactionUpdateToastMessageProps
> = (props): React.ReactNode => {
  const translate = useTranslate()
  return (
    <TestId testId={'bulkTransactionUpdatePartialCompleteToast'}>
      <div
        css={css`
          ${getFontCss(
            14,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate(
          'Only ${updated} of ${total} transactions could be updated.',
          {
            updated: <>{props.partial}</>,
            total: <>{props.total}</>,
          },
        )}
      </div>
    </TestId>
  )
}

export const SaasEnterpriseRequestSuccessToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    "We've received your request! A member from our team will be in touch with you shortly.",
  )
}

export const SaasEnterpriseRequestFailureToastMessage = (): React.ReactNode => {
  const translate = useTranslate()
  return translate(
    'Oops! There was an issue with your request. Please try again later or email us directly at billing@paywithextend.com.',
  )
}

export const SaasCancelSubscriptionSuccessToastMessage: React.FC<{
  tier?: SaasTierType
}> = (props): React.ReactNode => {
  const translate = useTranslate()
  return (
    <span>
      {translate(
        props.tier === 'ENTERPRISE'
          ? 'A message has been sent to our customer support team. They will reach out with the next steps to downgrade your plan.'
          : 'Your subscription has been cancelled. You will have access to your account until the end of your current billing cycle.',
      )}
    </span>
  )
}

export const SaasCancelSubscriptionFailureToastMessage =
  (): React.ReactNode => {
    const translate = useTranslate()
    return (
      <span>
        {translate(
          'Oops! There was an issue with your request. Please try again later or email us directly at billing@paywithextend.com.',
        )}
      </span>
    )
  }

const subscribeBodyClassChanges = (callback: () => void): (() => void) => {
  if (typeof document === 'undefined') return (): void => undefined
  const observer = new MutationObserver(callback)
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  })
  return (): void => observer.disconnect()
}

// Both BaseModal (via react-modal `bodyOpenClassName`) and App.tsx add
// `modal-open` to <body> when an overlay is up. The toast's white gradient
// halo assumes a light page underneath and looks wrong over a darkened modal
// backdrop, so we suppress it whenever this class is present.
const getBodyHasModalOpen = (): boolean =>
  typeof document !== 'undefined' &&
  document.body.classList.contains('modal-open')

const getBodyHasModalOpenServer = (): boolean => false

const Toast: React.FC = (): React.ReactNode => {
  const isToastOpen = useSelector(selectIsToastOpenFromState)
  const toastMessage = useSelector(selectToastMessageFromState)
  const hidableToastName = useSelector(selectHidableToastNameFromState)
  const inProgressColorScheme = useSelector(
    selectInProgressColorSchemeFromState,
  )
  const failureColorScheme = useSelector(selectFailureColorSchemeFromState)
  const partialSuccessScheme = useSelector(
    selectPartialSuccessColorSchemeFromState,
  )
  const hideImage = useSelector(selectHideImageFromState)
  const image = useSelector(selectImageFromState)
  const isInfinite = useSelector(selectIsInfinite)
  const isDarkBackground = useSelector(selectIsDarkBackground)
  const location = useSelector(selectLocationFromState)
  const forceHideToast = useSelector(selectForceHideToast)
  const bulkToast = useSelector(selectBulkToast)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const bodyHasModalOpen = useSyncExternalStore(
    subscribeBodyClassChanges,
    getBodyHasModalOpen,
    getBodyHasModalOpenServer,
  )

  // check if paths match exactly
  const pathMatch = matchPath(calculatedBackgroundLocation.pathname, {
    path: location.pathname,
    strict: true,
    exact: true,
  })

  const dispatch = useDispatch()

  const closeToastTimeout = useRef<NodeJS.Timeout | undefined>()
  useEffect(() => {
    if (!isInfinite) {
      if (isToastOpen) {
        closeToastTimeout.current = setTimeout((): void => {
          dispatch(
            createSetIsToastOpen(false, {
              toastMessage: toastMessage,
              inProgressColorScheme: inProgressColorScheme,
              failureColorScheme: failureColorScheme,
              hideImage: hideImage,
              image: image,
              isInfinite: isInfinite,
              isDarkBackground: isDarkBackground,
            }),
          )
        }, 7000)
      }
      return (): void => {
        if (closeToastTimeout.current !== undefined) {
          clearTimeout(closeToastTimeout.current)
        }
      }
    }
  }, [isToastOpen, toastMessage])

  // Portal to body so the toast escapes ancestor stacking contexts and stays
  // above react-modal overlays, which also portal directly to body.
  if (typeof document === 'undefined') return <></>

  return createPortal(
    <div
      css={css`
        position: fixed;
        height: 90px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        width: 100%;
        z-index: 11;
        bottom: -90px;

        transition:
          bottom ${styleConstants.transitionSpeed.slow} ease-out,
          opacity ${styleConstants.transitionSpeed.slow} ease-out;
        opacity: 0;

        ${isToastOpen &&
        (!bulkToast || !forceHideToast) &&
        css`
          bottom: 0px;
          opacity: 1;
        `}
      `}
    >
      {/* if paths don't match exactly, a flyout is probably open so render in dark background mode if no prop is passed in */}
      <div
        css={css`
          position: absolute;
          background-image: linear-gradient(
            rgba(255, 255, 255, 0.2),
            ${styleConstants.colors.white}
          );
          opacity: 0.9;
          height: 100%;
          width: 100%;

          ${!!(isDarkBackground || !pathMatch || bodyHasModalOpen) &&
          css`
            background-image: none;
          `}
        `}
      />
      <div
        css={css`
          width: 100%;
          height: 100%;
          z-index: 2;
          border-radius: 10px;
          box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px 20px;
          background-color: ${themeConstants.colors.highlight};
          ${getFontCss(
            14,
            styleConstants.colors.white,
            styleConstants.fontFamilies.regular,
          )};

          ${!!inProgressColorScheme &&
          css`
            ${LeftToRight()};
            background-image: linear-gradient(
              45deg,
              ${themeConstants.colors.highlight},
              ${themeConstants.colors.cta}
            );
            background-size: 500% 100%;
            height: 100%;
            width: 100%;
          `}

          ${!!failureColorScheme &&
          css`
            background-color: ${styleConstants.colors.red};
          `}

          ${!!partialSuccessScheme &&
          css`
            background-color: #ffe1af;
          `}
        `}
      >
        <div
          css={css`
            flex-grow: 2;
            display: flex;
            align-items: center;
          `}
        >
          {!hideImage && (
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                width: 24px;
                height: 24px;

                ${!image &&
                css`
                  background-color: ${styleConstants.colors.white};
                `}
              `}
            >
              {image || (
                <SvgCheck
                  css={css`
                    transform: scale(0.8);
                  `}
                />
              )}
            </div>
          )}
          <span
            css={css`
              margin-left: 20px;
              ${partialSuccessScheme
                ? css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.black,
                      styleConstants.fontFamilies.regular,
                    )};
                  `
                : css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.white,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
            `}
          >
            {toastMessage}
          </span>
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <CloseButton
            id={'toastCloseBtn'}
            design={
              failureColorScheme
                ? CloseButtonDesigns.RedLightOnDark
                : CloseButtonDesigns.LightOnDark
            }
            suppressCloseModalAction
            onClick={(): void => {
              if (hidableToastName !== undefined) {
                dispatch(createSetHideToast(hidableToastName))
              }
              dispatch(
                createSetIsToastOpen(false, {
                  toastMessage: toastMessage,
                  inProgressColorScheme: inProgressColorScheme,
                  failureColorScheme: failureColorScheme,
                  hideImage: hideImage,
                  image: image,
                  isInfinite: isInfinite,
                  isDarkBackground: isDarkBackground,
                }),
              )
            }}
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default Toast
